import { FC, Fragment, useCallback, useEffect, useState } from "react"

import { yupResolver } from "@hookform/resolvers/yup"
import {
  Autocomplete,
  Box,
  Checkbox,
  debounce,
  Stack,
  TextField as MuiTextField,
} from "@mui/material"
import { useMutationUpdateSchoolGroup } from "api/reactQuery/mutations/schoolGroups"
import {
  useInfiniteQuerySchoolGroupCandidates,
  useInfiniteQuerySchoolGroupMembers,
} from "api/reactQuery/queries/schoolGroups"
import Dialog from "components/common/dialog/dialog"
import TextField from "components/form/common/field/textField"
import { useCustomSnackbar } from "hooks/snackbar"
import { getGroupMutationFeedback } from "pages/groups/components/getGroupMutationFeedback"
import { renderGroupMemberTags } from "pages/groups/components/renderGroupMemberTags"
import { useGroupMembersListboxMaxHeight } from "pages/groups/components/useGroupMembersListboxMaxHeight"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { IGroup } from "ts/interfaces/Group"
import { IObjectStudentDetailed } from "ts/interfaces/Student"
import { requiredStringSchema } from "utils/yup"
import * as yup from "yup"

// Structurally satisfied by both IGroupMember (initial value) and the candidates search
// results so the same Autocomplete can mix pre-selected members with search results
// without converting either shape.
type IMemberOption = Pick<
  IObjectStudentDetailed,
  "id" | "first_name" | "last_name"
>

interface IFormValues {
  name: string
  members: IMemberOption[]
}

const schema = yup.object({
  name: requiredStringSchema,
  members: yup.array().default([]),
})

interface IProps {
  open: boolean
  group: IGroup
  onClose: () => void
}

const EditSchoolGroupDialog: FC<IProps> = ({ open, group, onClose }) => {
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()
  const [search, setSearch] = useState("")
  const [isMembersFocused, setIsMembersFocused] = useState(false)
  const {
    listboxMaxHeight,
    onOpen: onListboxOpen,
    onClose: onListboxClose,
  } = useGroupMembersListboxMaxHeight()

  const defaultValues: IFormValues = {
    name: group.name,
    members: group.members_preview.map((member) => ({
      id: member.id,
      first_name: member.first_name ?? "",
      last_name: member.last_name ?? "",
    })),
  }

  const methods = useForm<IFormValues>({
    resolver: yupResolver(schema),
    defaultValues,
  })

  const needsFullMembers = group.members_count > group.members_preview.length

  const {
    data: fullMembersData,
    isLoading: isLoadingFullMembers,
    hasNextPage: hasMoreFullMembers,
    isFetchingNextPage: isFetchingMoreFullMembers,
    fetchNextPage: fetchNextFullMembersPage,
  } = useInfiniteQuerySchoolGroupMembers({
    groupId: group.id,
    options: { enabled: open && needsFullMembers },
  })

  useEffect(() => {
    if (hasMoreFullMembers && !isFetchingMoreFullMembers) {
      fetchNextFullMembersPage()
    }
  }, [hasMoreFullMembers, isFetchingMoreFullMembers, fetchNextFullMembersPage])

  const isLoadingAllMembers =
    needsFullMembers &&
    (isLoadingFullMembers || hasMoreFullMembers || isFetchingMoreFullMembers)

  useEffect(() => {
    if (!fullMembersData || hasMoreFullMembers) return
    methods.setValue(
      "members",
      fullMembersData.pages.flatMap((page) =>
        page.data.objects.map((member) => ({
          id: member.id,
          first_name: member.first_name ?? "",
          last_name: member.last_name ?? "",
        })),
      ),
      { shouldDirty: false },
    )
  }, [fullMembersData, hasMoreFullMembers, methods.setValue])

  const { data, isFetching, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteQuerySchoolGroupCandidates({
      groupId: group.id,
      filter: search,
      options: { enabled: open, keepPreviousData: true },
    })
  const options =
    data?.pages.flatMap((page) =>
      page.data.objects.map((member) => ({
        id: member.id,
        first_name: member.first_name ?? "",
        last_name: member.last_name ?? "",
      })),
    ) ?? []

  const { ref: loadMoreRef, inView: isLoadMoreInView } = useInView()

  useEffect(() => {
    if (isLoadMoreInView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [isLoadMoreInView])

  const debouncedSetSearch = useCallback(debounce(setSearch, 300), [])

  const { mutate, isLoading } = useMutationUpdateSchoolGroup({
    options: {
      onSuccess: () => {
        showSnackbar({ title: t("group.groupUpdatedSuccess") })
        handleClose()
      },
      onError: (error) => {
        const { onNameField, message, isRaw, nameError } =
          getGroupMutationFeedback(error)
        if (onNameField && nameError) {
          methods.setError("name", nameError)
          return
        }
        showSnackbar({
          title: isRaw ? message : t(message),
          variant: "error",
        })
      },
    },
  })

  const handleClose = () => {
    methods.reset(defaultValues)
    setSearch("")
    setIsMembersFocused(false)
    onClose()
  }

  const onSubmit = methods.handleSubmit((values) => {
    if (isLoading) return
    mutate({
      groupId: group.id,
      data: {
        name: values.name,
        student_ids: values.members.map((member) => member.id),
      },
    })
  })

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      titleText={t("group.editGroup")}
      tabletMaxWidth="440px"
      actionAcceptText={t("save")}
      isLoading={isLoading}
      actionAcceptButtonProps={{ disabled: isLoading || isLoadingAllMembers }}
      onActionButtonClick={onSubmit}
    >
      <FormProvider {...methods}>
        <Stack gap="24px" component="form" onSubmit={onSubmit}>
          <TextField
            name="name"
            label={t("group.groupNameLabel")}
            placeholder={t("group.groupNameLabel")}
            fullWidth
          />
          <Controller
            name="members"
            control={methods.control}
            render={({ field: { value, onChange } }) => (
              <Autocomplete
                multiple
                disableCloseOnSelect
                loading={isFetching}
                options={options}
                value={value}
                ListboxProps={{
                  sx: { maxHeight: listboxMaxHeight },
                }}
                onOpen={onListboxOpen}
                onClose={onListboxClose}
                onFocus={() => setIsMembersFocused(true)}
                onBlur={() => setIsMembersFocused(false)}
                onChange={(_, newValue) => onChange(newValue)}
                onInputChange={(_, newInputValue) =>
                  debouncedSetSearch(newInputValue)
                }
                isOptionEqualToValue={(option, val) => option.id === val.id}
                getOptionLabel={(option) =>
                  `${option.first_name} ${option.last_name}`
                }
                renderOption={(props, option, { selected }) => {
                  const { key: _key, ...optionProps } = props
                  const isLastOption =
                    option.id === options[options.length - 1]?.id
                  return (
                    <Fragment key={option.id}>
                      <Box component="li" {...optionProps}>
                        <Checkbox checked={selected} sx={{ mr: "8px" }} />
                        {option.first_name} {option.last_name}
                      </Box>
                      {isLastOption && hasNextPage && <Box ref={loadMoreRef} />}
                    </Fragment>
                  )
                }}
                renderTags={(members, getTagProps) =>
                  renderGroupMemberTags(isMembersFocused, members, getTagProps)
                }
                renderInput={(params) => (
                  <MuiTextField
                    {...params}
                    label={t("group.searchStudentLabel")}
                    placeholder={
                      value.length === 0
                        ? t("group.searchStudentLabel")
                        : undefined
                    }
                  />
                )}
              />
            )}
          />
        </Stack>
      </FormProvider>
    </Dialog>
  )
}

export default EditSchoolGroupDialog
